import {
  CheckCircleOutlined,
  LoadingOutlined,
  RobotOutlined,
  SendOutlined,
  StopOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { useChat } from "@ai-sdk/react";
import {
  DefaultChatTransport,
  getToolName,
  isToolUIPart,
  type DynamicToolUIPart,
  type ToolUIPart,
  type UIMessage,
} from "ai";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Typography } from "antd";
import type React from "react";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { Streamdown } from "streamdown";

import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "@fitness-app/web-ui/components/ai-elements/conversation";
import { Loader } from "@fitness-app/web-ui/components/ai-elements/loader";
import { Message, MessageContent, MessageResponse } from "@fitness-app/web-ui/components/ai-elements/message";
import {
  PromptInput,
  PromptInputBody,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
} from "@fitness-app/web-ui/components/ai-elements/prompt-input";
import { Reasoning, ReasoningContent, ReasoningTrigger } from "@fitness-app/web-ui/components/ai-elements/reasoning";

import { getClientWebApiBaseUrl } from "~/modules/Sales/SalesAgent/constants";
import { supabase } from "~/store/initializeStore";
import { SUPPORT_REQUESTS_QUERY_KEY } from "../supportConstants";

type SupportToolPart = ToolUIPart | DynamicToolUIPart;

const SUPPORT_AGENT_SUGGESTIONS = [
  "Klientka nie może zmienić karty do płatności",
  "Klientka opłaciła ratę, a nie ma dostępu do aplikacji",
  "Błąd w aplikacji — opiszę szczegóły",
];

const TOOL_LABELS: Record<string, string> = {
  findClientTool: "Szukam klienta...",
  getClientOrdersTool: "Sprawdzam zamówienia...",
  createSupportRequestTool: "Tworzę zgłoszenie...",
};

const getCreatedRequestSummary = (part: SupportToolPart) => {
  if (getToolName(part) !== "createSupportRequestTool" || part.state !== "output-available") {
    return null;
  }

  const output = part.output as { requestId?: string; linearIssueIdentifier?: string | null; info?: string } | null;
  return output?.requestId ? output : null;
};

const SupportToolCall = ({ part }: { part: SupportToolPart }) => {
  const toolName = String(getToolName(part));
  const isDone = part.state === "output-available" || part.state === "output-error";

  const createdRequest = getCreatedRequestSummary(part);

  if (createdRequest) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
        <CheckCircleOutlined />
        <span>
          {createdRequest.info ||
            `Zgłoszenie utworzone${createdRequest.linearIssueIdentifier ? ` (${createdRequest.linearIssueIdentifier})` : ""}.`}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-xs text-gray-400">
      {isDone ? <ToolOutlined /> : <LoadingOutlined spin />}
      <span>{TOOL_LABELS[toolName] || toolName}</span>
    </div>
  );
};

const SupportMessageParts = ({
  isLastMessage,
  isStreaming,
  message,
}: {
  isLastMessage: boolean;
  isStreaming: boolean;
  message: UIMessage;
}) => {
  const parts = message.parts ?? [];
  const reasoningParts = parts.filter((part) => part.type === "reasoning");
  const reasoningText = reasoningParts
    .map((part) => part.text)
    .join("\n\n")
    .trim();
  const lastPart = parts.at(-1);
  const isReasoningStreaming = isLastMessage && isStreaming && lastPart?.type === "reasoning";
  const hasVisibleContent =
    Boolean(reasoningText) ||
    parts.some((part) => {
      if (part.type === "text") return part.text.trim();
      return isToolUIPart(part);
    });

  return (
    <div className="space-y-2">
      {reasoningText ? (
        <Reasoning className="w-full" isStreaming={isReasoningStreaming}>
          <ReasoningTrigger />
          <ReasoningContent>{reasoningText}</ReasoningContent>
        </Reasoning>
      ) : null}
      {parts.map((part, index) => {
        if (part.type === "text") {
          if (!part.text.trim()) {
            return null;
          }
          return (
            <MessageResponse key={`${message.id}-text-${index}`}>
              <Streamdown
                controls={false}
                mode={isLastMessage && isStreaming ? "streaming" : "static"}
                parseIncompleteMarkdown
              >
                {part.text}
              </Streamdown>
            </MessageResponse>
          );
        }

        if (!isToolUIPart(part)) return null;

        return <SupportToolCall key={`${message.id}-${part.toolCallId}`} part={part} />;
      })}
      {isLastMessage && isStreaming && !hasVisibleContent ? <Loader /> : null}
    </div>
  );
};

const SupportAgentPrompt = memo(
  ({
    isBusy,
    onStop,
    onSubmit,
    status,
  }: {
    isBusy: boolean;
    onStop: () => void;
    onSubmit: (value: string) => void;
    status: "ready" | "submitted" | "streaming" | "error";
  }) => {
    const [input, setInput] = useState("");

    const handleSubmit = useCallback(
      (value: string) => {
        onSubmit(value);
        setInput("");
      },
      [onSubmit],
    );

    const handlePromptKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;

      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }, []);

    return (
      <PromptInput
        className="rounded-lg border-gray-200 bg-white shadow-none"
        onSubmit={(payload) => handleSubmit(payload.text)}
      >
        <PromptInputBody className="px-4 pt-3">
          <PromptInputTextarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handlePromptKeyDown}
            disabled={isBusy}
            rows={3}
            placeholder="Opisz problem do zgłoszenia..."
          />
        </PromptInputBody>
        <PromptInputFooter className="px-3 pb-3 pt-2">
          <PromptInputTools />
          {isBusy ? (
            <Button shape="circle" icon={<StopOutlined />} onClick={onStop} aria-label="Zatrzymaj odpowiedź" />
          ) : (
            <PromptInputSubmit disabled={!input.trim()} status={status}>
              <SendOutlined />
              <span className="sr-only">Wyślij</span>
            </PromptInputSubmit>
          )}
        </PromptInputFooter>
      </PromptInput>
    );
  },
);

SupportAgentPrompt.displayName = "SupportAgentPrompt";

export const SupportAgentChat = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const activeSessionIdRef = useRef<string | null>(null);
  const [, setActiveSessionId] = useState<string | null>(null);

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: `${getClientWebApiBaseUrl()}/api/support/agent-chat`,
        fetch: async (input, init) => {
          const response = await fetch(input, init);
          const responseSessionId = response.headers.get("X-Agent-Chat-Session-Id");

          if (responseSessionId) {
            activeSessionIdRef.current = responseSessionId;
            setActiveSessionId(responseSessionId);
          }

          return response;
        },
        prepareSendMessagesRequest: async ({ api, body, headers, credentials, id, messages, trigger, messageId }) => {
          const token = (await supabase.auth.getSession()).data.session?.access_token;
          const latestMessage = messages.at(-1);

          return {
            api,
            body: {
              ...body,
              id,
              sessionId: activeSessionIdRef.current ?? undefined,
              messages: latestMessage ? [latestMessage] : [],
              trigger,
              messageId,
            },
            credentials,
            headers: {
              ...headers,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          };
        },
      }),
    [],
  );

  const { messages, sendMessage, setMessages, status, stop, error, clearError } = useChat<UIMessage>({
    transport,
    experimental_throttle: 50,
    onFinish: async () => {
      // A ticket may have been created mid-conversation — refresh the board.
      await queryClient.invalidateQueries({ queryKey: [SUPPORT_REQUESTS_QUERY_KEY] });
    },
  });

  const isBusy = status === "submitted" || status === "streaming";

  const startNewConversation = useCallback(() => {
    if (isBusy) return;
    activeSessionIdRef.current = null;
    setActiveSessionId(null);
    clearError();
    setMessages([]);
  }, [clearError, isBusy, setMessages]);

  const submit = useCallback(
    (value: string) => {
      const text = value.trim();
      if (!text || isBusy) return;

      clearError();
      void sendMessage({
        id: crypto.randomUUID(),
        role: "user",
        parts: [{ type: "text", text }],
      });
    },
    [clearError, isBusy, sendMessage],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between pb-3">
        <Typography.Text type="secondary" className="text-xs">
          Agent zbierze szczegóły i sam utworzy zgłoszenie na boardzie.
        </Typography.Text>
        <Button size="small" onClick={startNewConversation} disabled={isBusy || messages.length === 0}>
          Nowa rozmowa
        </Button>
      </div>

      <Conversation>
        <ConversationContent className="px-1 py-2">
          {messages.length ? (
            messages.map((message, index) => (
              <Message key={message.id} from={message.role}>
                <MessageContent
                  className={
                    message.role === "user"
                      ? "rounded-[18px] border-gray-200 bg-gray-100 px-3 py-2 text-gray-950 shadow-none"
                      : "border-transparent bg-transparent px-0 py-0 text-gray-900 shadow-none"
                  }
                >
                  <SupportMessageParts
                    message={message}
                    isLastMessage={index === messages.length - 1}
                    isStreaming={status === "streaming"}
                  />
                </MessageContent>
              </Message>
            ))
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-4">
              <ConversationEmptyState
                icon={<RobotOutlined className="text-2xl" />}
                title="Zgłoś problem"
                description="Opisz co się dzieje — agent dopyta o szczegóły, sprawdzi klienta i sam utworzy zgłoszenie."
              />
              <div className="flex max-w-md flex-col gap-2">
                {SUPPORT_AGENT_SUGGESTIONS.map((suggestion) => (
                  <Button
                    key={suggestion}
                    size="small"
                    className="whitespace-normal"
                    onClick={() => submit(suggestion)}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {status === "submitted" ? (
            <Message from="assistant">
              <MessageContent className="border-transparent bg-transparent px-0 py-0 text-gray-500 shadow-none">
                <Loader />
              </MessageContent>
            </Message>
          ) : null}
          {error ? <Typography.Text type="danger">{error.message}</Typography.Text> : null}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      <div className="shrink-0 pt-3">
        <SupportAgentPrompt isBusy={isBusy} onStop={stop} onSubmit={submit} status={status} />
      </div>
    </div>
  );
};
